<template>
    <div class="poi-list" :class="{'poi-list--escape-wrapper': escapeWrapper}">
        <ContentLoader class="sidebar__section" v-if="!sortedPOIs.length && loading" />
        <div class="poi-list__list" :class="{'is-loading': loading}">
            <template v-for="(poi, index) in sortedPOIs">
                <div class="sidebar__section sidebar__section--larger-top-vertical-padding" v-if="currentSorting === 'PRICE_ASC' && index === notCheapestMessageIndex" :class="{ 'sidebar__section--larger-top-vertical-padding--top-smaller' : notCheapestMessageIndex === 0}" :key="`resultinfo-${index}`">
                    <span class="caption-light">Nicht unter den Günstigsten</span>
                </div>
                <POIResultsItem :poi="poi" :key="`resultitem-${index}`" :mapConnectionDisabled="mapConnectionDisabled" :detailPageDisabled="printStyle" :printStyle="printStyle" />
            </template>
            <NoResultsMessage :face="false" :icon="true" :type="noResultsMessageType" v-if="!loading && !sortedPOIs.length && !showTrafficLegend" :poiSystemError="showSystemError">
                <IconStarOutline slot="icon" v-if="selectedPOIs.includes('favourites')" />
                <POIIcon slot="icon" :icon="noResultsIcon"  v-else-if="noResultsIcon"/>
                <IconPinBig slot="icon" v-else/>
                <a slot="login" class="form__section__link" v-if="selectedPOIs.includes('favourites') && !userLoggedIn" @click.prevent="openLoginMenuClickHandler">Jetzt einloggen</a>
            </NoResultsMessage>

            <div class="poi-list__traffic-legend" v-if="showTrafficLegend">
                <TrafficLegend :singleColumn="true" />
            </div>
        </div>
        <div class="poi-list__load-trigger" ref="load-trigger"></div>
        <div class="poi-list__load-more ui-center-content" v-if="moreLoading">
            <Loader />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getPoiId, getBasicPreparedPoiData } from '@/utils/poi-utils'
import eventBus from '@/utils/event-bus';

import POIResultsItem from '@/components/POIResultsItem.vue'
import POIIcon from '@/components/POIIcon.vue'
import IconPinBig from '@/components/icons/IconPinBig.vue'
import IconStarOutline from '@/components/icons/IconStarOutline.vue'
import Loader from '@/components/Loader.vue'
import ContentLoader from '@/components/ContentLoader.vue'

export default {
    components: {
        POIIcon,
        IconPinBig,
        IconStarOutline,
        POIResultsItem,
        Loader,
        ContentLoader,
        NoResultsMessage: () => import(/* webpackChunkName: "NoResultsMessage" */ '@/components/NoResultsMessage.vue'),
        TrafficLegend: () => import(/* webpackChunkName: "TrafficLegend" */ '@/components/TrafficLegend.vue'),
    },
    props: {
        pois: {
            default: () => {},
            type: Object
        },
        loading: {
            default: false,
            type: Boolean
        },
        moreLoading: {
            default: false,
            type: Boolean
        },
        onlyType: {
            default: false,
            type: [String, Boolean]
        },
        active: {
            default: true,
            type: Boolean
        },
        currentSorting: {
            default: false,
            type: [String, Boolean]
        },
        noResultsIcon: {
            default: false,
            type: [String, Boolean]
        },
        escapeWrapper: {
            default: false,
            type: Boolean
        },
        mapConnectionDisabled: {
            type: Boolean,
            default: false
        },
        printStyle: {
            type: Boolean,
            default: false
        },
    },
    data() {
      return {
        highlightedPoiId: false,
      };
    },
    computed: {
        ...mapGetters(['routeCorridor', 'user', 'selectedPOIs', 'poiApiErrors', 'poiSettings']),
        sortedPOIs() {
            if(!this.active) return false;
            let activePOITypeCount = 0;
            const allPOIs = []
            for(let poiType in this.pois) {
                if(!this.pois[poiType] || !this.pois[poiType].length) continue;
                if(poiType === 'favourites' && !this.userLoggedIn) {
                    this.$store.dispatch('deselectPOI', poiType);
                    continue;
                }
                activePOITypeCount++;
                this.pois[poiType].forEach(poiResult => {
                    let preparedData = getBasicPreparedPoiData(poiType, poiResult);
                    preparedData.highlight = this.getHighlight(poiType, poiResult);
                    allPOIs.push(preparedData)
                })
            }

            

            // additional front-end sorting to mix results from multiple poi categories
            if(activePOITypeCount >= 1 && this.selectedPOIs.length > 1) {
                allPOIs.sort((a, b) => {
                    switch(this.currentSorting) {
                        case 'DISTANCE_ASC': {
                            return (a.distance > b.distance) ? 1 : -1
                        }
                        case 'NAME_ASC': {
                            return (a.title > b.title) ? 1 : -1
                        }
                    }
                })
            }
            return allPOIs
        },
        visiblePoiCounts() {
            let counts = {};
            for(let poiType in this.pois) {
                if(!this.pois[poiType]) return false;
                if(poiType === 'favourites') {
                    counts[poiType] = this.sortedPOIs ? this.sortedPOIs.filter(poi => poi.isFavouritePoi === true).length : 0;
                } else {
                    counts[poiType] = this.sortedPOIs ? this.sortedPOIs.filter(poi => poi.type === poiType).length : 0;
                }
            }   
            return counts;
        },
        resultsExist() {
            let response = false;
            for(let poiType in this.pois) {
                if(!this.pois[poiType]) return false;
                response = true;
            }
            return response;
        },
        noResultsMessageType() {
            if(this.routeCorridor){
                return this.resultsExist ? 'NO_VISIBLE_RESULTS' : 'POI_ROUTE';
            } else if(this.selectedPOIs.includes('favourites') && this.userLoggedIn){
                return 'POI_FAVOURITES';
            } else if(this.selectedPOIs.includes('favourites') && !this.userLoggedIn){
                return 'POI_FAVOURITES_NOT_LOGGED_IN';
            } else {
                return 'POI_GENERAL';
            }
        },
        showSystemError() {
            for (const poi of this.selectedPOIs) {
                if(this.poiApiErrors[poi]) {
                    return true
                }
            }
            return false
        },
        notCheapestMessageIndex() {
            const poisWithPrice = this.sortedPOIs.filter(poi => poi.price && poi.price !== false)
            return poisWithPrice.length
        },
        userLoggedIn() {
            return this.user;
        },
        showTrafficLegend() {
            if(this.selectedPOIs.length === 1 && this.selectedPOIs[0] === 'traffic') {
                return !this.loading && !this.sortedPOIs.length && this.poiSettings.traffic.type.length === 1 && this.poiSettings.traffic.type[0] === 'Verkehrsfluss'
            }
            return false
        }
    },
    mounted() {
        this.addListeners();
        this.addIntersectionObserver();
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        addListeners() {
            if(!this.mapConnectionDisabled) {
                eventBus.$on('toggleHighlightPoi', this.toggleHighlightPoiHandler);
            }
        },
        removeListeners() {
            eventBus.$off('toggleHighlightPoi', this.toggleHighlightPoiHandler);
        },
        toggleHighlightPoiHandler(data) {
            this.toggleHighlightedPoiId(data);
        },
        toggleHighlightedPoiId(data) {
            if(data.highlight) {
                this.highlightedPoiId = data.id;
            } else {
                this.highlightedPoiId = false;
            }
        },
        getHighlight(poiType, poiResult) {
            return this.highlightedPoiId === getPoiId(poiType, poiResult);
        },

        addIntersectionObserver() {
            let observer = new IntersectionObserver((entries) => { 
                if(!this.active) return false;
                entries.forEach(entry => {
                    if(entry.isIntersecting){
                        this.loadMore();
                    }
                });
            });
            observer.observe(this.$refs['load-trigger']);
        },
        loadMore() {
            if(!this.resultsExist || this.loading) return false;
            this.$emit('load-more', { counts: this.visiblePoiCounts });
        },
        openLoginMenuClickHandler() {
            eventBus.$emit('showLoginMenu');
        },
    }
}
</script>

<style lang="scss">
.poi-list {
    &--escape-wrapper {
        margin-left: -16px;
        margin-right: -16px;
    }
    &__list {
        transition: opacity 0.2s;
        &.is-loading {
            opacity: 0.5;
            pointer-events: none;
        }
    }
    &__load-trigger {
        height: 1px;
    }
    &__load-more {
        padding: 32px 0;
    }

    &__traffic-legend {
        padding: 50px 42px;
    }
}
</style>